import { FormlyFieldConfig } from '@ngx-formly/core';
import { getUnitSymbol } from '../shared/helpers/unit-display.map';
import { TypedOptionEntry, ValueEntity } from '../pages/products/product.model';

/** A single item in a dropdown / select list, compatible with `EnumOption`, `StringOption`, and `SelectOption` from `@gea/digital-ui-lib`. */
export interface DropdownOption<T> {
  name: string;
  value: T;
  disabled: boolean;
}

/** Columns used when no child in a container declares a colspan (plain wrapping list). */
const DEFAULT_GRID_COLUMNS = 2;

/** Upper bound for the grid classes; must match the SCSS loop in styles.scss. */
const MAX_GRID_COLUMNS = 12;

// ─── ValueEntity helpers ──────────────────────────────────────────────────────

/** Returns true when the object looks like a ValueEntity<T> from PPM. */
export function isValueEntity(obj: any): obj is ValueEntity {
  return obj != null && typeof obj === 'object' && 'IsVisible' in obj && 'IsEnabled' in obj && 'Value' in obj;
}

/**
 * Converts a TypedOptions dictionary to a `SelectOption`-compatible array.
 * Uses `name` (not `label`) because `EnumOption` / `StringOption` / `SelectOption`
 * from `@gea/digital-ui-lib` all use the `name` property as the display text.
 */
export function typedOptionsToArray<T>(typedOptions: Record<string, TypedOptionEntry<T>>): DropdownOption<T>[] {
  return Object.values(typedOptions).map((entry) => ({
    name: entry.DisplayKey,
    value: entry.Value,
    disabled: !entry.IsEnabled,
  }));
}

/** Extracts the first error message from ValueEntity Logs, or null if none. */
function extractErrorFromLogs(logs: ValueEntity['Logs'] | null | undefined): string | null {
  if (!logs || logs.length === 0) return null;
  const errorLog = logs.find((l) => l['Level'] === 'Error' || l['Severity'] === 'Error');
  return (errorLog?.['Message'] as string) ?? (logs[0]?.['Message'] as string) ?? null;
}

/** Applies a single ValueEntity to a Formly field config (value, visibility, enabled, options, unit, errors). */
function applyValueEntityToField(field: FormlyFieldConfig, entity: ValueEntity): void {
  // Value
  field.defaultValue = entity.Value;
  if (field.formControl && entity.Value !== undefined) {
    field.formControl.setValue(entity.Value, { emitEvent: false });
    field.formControl.markAsPristine();
  }

  // Visibility — propagated to className by applyVisibilityState() called afterwards
  field.props = { ...(field.props ?? {}), visible: entity.IsVisible };

  // Enabled / disabled
  // Set both props.disabled (used by Formly on initial render & isDisabled getter)
  // and the formControl (for live updates after form creation).
  field.props = { ...(field.props ?? {}), disabled: !entity.IsEnabled };
  if (field.formControl) {
    if (entity.IsEnabled) {
      field.formControl.enable({ emitEvent: false });
    } else {
      field.formControl.disable({ emitEvent: false });
    }
  }

  if (entity.TypedOptions) {
    field.props = { ...field.props, typedOptions: entity.TypedOptions };
  }

  // Unit display — uses `displayUnit` to match the geaui-input-v3 template
  // which renders {{ props['displayUnit'] ?? '' }}
  const unitSymbol = getUnitSymbol(entity.Unit);
  if (unitSymbol) {
    field.props = { ...(field.props ?? {}), displayUnit: unitSymbol };
  }

  // Errors from Logs
  const errorMsg = extractErrorFromLogs(entity.Logs);
  field.props = { ...(field.props ?? {}), errorMessage: errorMsg ?? undefined };
}

/**
 * Recursively update only the fields that already exist by key.
 */
export function patchExistingFieldsOnly(original: FormlyFieldConfig[], updated: FormlyFieldConfig[]): void {
  const originalMap = new Map<string, FormlyFieldConfig>();
  for (const field of original) {
    if (field.key != null) {
      originalMap.set(String(field.key), field);
    }
  }

  for (const updatedField of updated) {
    const updatedKey = updatedField.key != null ? String(updatedField.key) : null;
    if (!updatedKey) {
      continue;
    }

    const existingField = originalMap.get(updatedKey);
    if (existingField) {
      updateField(existingField, updatedField);
    }
  }
}

function updateField(originalField: FormlyFieldConfig, updatedField: FormlyFieldConfig): void {
  originalField.props = updatedField.props;
  originalField.templateOptions = updatedField.templateOptions;
  originalField.className = updatedField.className;
  originalField.fieldGroupClassName = updatedField.fieldGroupClassName;

  if (updatedField.fieldGroup && originalField.fieldGroup) {
    patchExistingFieldsOnly(originalField.fieldGroup, updatedField.fieldGroup);
  }
}

function getColspan(item: any): number | undefined {
  const colspan = item?.props?.colspan;
  if (typeof colspan !== 'number' || !Number.isInteger(colspan) || colspan < 1) return undefined;
  return Math.min(colspan, MAX_GRID_COLUMNS);
}

/** Splits items into those that declare a colspan and those that don't. */
function classifyItems(items: any[]): { declared: any[]; undeclared: any[] } {
  const declared: any[] = [];
  const undeclared: any[] = [];
  items.forEach((item) => (getColspan(item) !== undefined ? declared : undeclared).push(item));
  return { declared, undeclared };
}

/** Sum of colspans for items known to all declare one, clamped to {@link MAX_GRID_COLUMNS}. */
function sumColspans(items: any[]): number {
  return Math.min(
    items.reduce((total: number, item) => total + getColspan(item)!, 0),
    MAX_GRID_COLUMNS
  );
}

/**
 * Resolves the column count of a container from its layout props:
 * `TopToBottom` stacks into one column. When every item (or no item) declares
 * a colspan, columns are the sum of the declared colspans (so `1` + `2`
 * renders as a 1:2 split in a single row), or {@link DEFAULT_GRID_COLUMNS}
 * when none declare one. When only *some* children declare a colspan, this
 * container becomes a single-column stack of two rows (see
 * {@link buildChildFieldGroup}), so it resolves to `1` here too.
 */
function resolveGridColumns(props: any): number {
  if (props?.orientation === 'TopToBottom') return 1;

  const items: any[] = Array.isArray(props?.items) ? props.items : [];
  if (!items.length) return DEFAULT_GRID_COLUMNS;

  const { declared, undeclared } = classifyItems(items);
  if (declared.length === 0) return DEFAULT_GRID_COLUMNS;
  if (undeclared.length > 0) return 1;

  return sumColspans(declared);
}

/** Grid classes for a container, used as Formly's `fieldGroupClassName`. */
export function buildGridContainerClasses(props: any): string {
  return `form-grid form-grid-cols-${resolveGridColumns(props)}`;
}

/**
 * Span class for a child, or undefined when it declares no colspan — leaving
 * `className` untouched so Formly type `defaultOptions` (e.g. `fullWidthComponent`)
 * still apply.
 */
function buildGridItemClass(item: any, containerColumns: number): string | undefined {
  const colspan = getColspan(item);
  return colspan === undefined ? undefined : `form-grid-span-${Math.min(colspan, containerColumns)}`;
}

/**
 * Builds the fieldGroup children for a container's items.
 *  - No items → empty.
 *  - Vertical orientation → all children stacked in one column.
 *  - Every item (or no item) declares a colspan → one flat row, sized by the
 *    sum of declared colspans (or {@link DEFAULT_GRID_COLUMNS} if none declare one).
 *  - Only *some* items declare a colspan → declared items keep their explicit
 *    ratio in their own row; undeclared siblings are grouped into a second,
 *    separate row that wraps at {@link DEFAULT_GRID_COLUMNS} instead of being
 *    squeezed into slivers of the ratio row's (unrelated) column count.
 */
function buildChildFieldGroup(props: any, schema: any, opId?: string): FormlyFieldConfig[] {
  const items: any[] = Array.isArray(props?.items) ? props.items : [];
  if (!items.length) return [];

  if (props?.orientation === 'TopToBottom') {
    return items.map((subItem) => convertItem(subItem, schema, opId, 1));
  }

  const { declared, undeclared } = classifyItems(items);

  if (declared.length === 0) {
    return items.map((subItem) => convertItem(subItem, schema, opId, DEFAULT_GRID_COLUMNS));
  }
  if (undeclared.length === 0) {
    const columns = sumColspans(declared);
    return items.map((subItem) => convertItem(subItem, schema, opId, columns));
  }

  // Synthetic wrapper groups carry no `key` of their own, so `id` must be set
  // to `opId` explicitly — applySchemaToFields() recurses via `field.id` as
  // the next call's opId, and an undefined `id` here would otherwise drop the
  // operation-point tab id for every field nested inside these sub-groups
  // (breaking value/unit/visibility lookups for DynamicTabList children).
  const ratioColumns = sumColspans(declared);
  return [
    {
      id: opId,
      fieldGroupClassName: `form-grid form-grid-cols-${ratioColumns}`,
      fieldGroup: declared.map((subItem) => convertItem(subItem, schema, opId, ratioColumns)),
    },
    {
      id: opId,
      fieldGroupClassName: `form-grid form-grid-cols-${DEFAULT_GRID_COLUMNS}`,
      fieldGroup: undeclared.map((subItem) => convertItem(subItem, schema, opId, DEFAULT_GRID_COLUMNS)),
    },
  ];
}

export function convertLayoutToFormly(layout: any[], schema: any): FormlyFieldConfig[] {
  const fields: FormlyFieldConfig[] = [];

  layout.forEach((item) => {
    if (item.panels) {
      item.panels.forEach((panel: any) => {
        fields.push(convertItem(panel, schema));
      });
    } else {
      fields.push(convertItem(item, schema));
    }
  });

  return fields;
}

function convertItem(item: any, schema: any, opId?: string, containerColumns: number = DEFAULT_GRID_COLUMNS): FormlyFieldConfig {
  let field: FormlyFieldConfig = {};

  // Honour the colspan the layout declares for this item within its parent grid.
  // Left undefined when the layout is silent, so Formly type defaultOptions still apply.
  const gridItemClass = buildGridItemClass(item, containerColumns);
  if (gridItemClass) {
    field.className = gridItemClass;
  }

  if (item.type === 'Panel' || item.type === 'Section' || item.type === 'Tab') {
    field.wrappers = [getPanelWrapper(item)];
    field.props = item.props;
    field.fieldGroupClassName = buildGridContainerClasses(item.props);
    field.id = opId;
    field.fieldGroup = buildChildFieldGroup(item.props, schema, opId);
  } else if (item.type === 'DynamicTabList') {
    field.key = item.key as string;
    field.type = mapType(item.type);
    field.props = item.props;
    if (item.props && Array.isArray(item.props.items) && item.props.items.length) {
      const collection = getSchemaForField(field.key, schema) ?? {};
      field.fieldGroup = [];

      field.fieldArray = {
        fieldGroup: Object.values(collection).map((op: any) => ({
          id: op.Id,
          fieldGroup: buildChildFieldGroup(item.props, schema, op.Id),
        })),
      } as any;
    }
  } else {
    field.key = item.key as string;
    if (opId) {
      field.key = item.key + '+' + opId;
    }
    field.type = mapType(item.type);
    field.props = item.props;
  }

  return field;
}

function mapType(type: string): string {
  const typeMap: { [key: string]: string } = {
    Panel: '',
    Section: '',
    Image: 'image',
    Select: 'select',
    NumberBox: 'number',
    DynamicTabList: 'dynamicalTabList',
    Spacer: 'spacer',
    Divider: 'divider',
    Tab: '',
    Input: 'input',
  };
  return typeMap[type] !== undefined ? typeMap[type] : type.toLowerCase();
}

/**
 * Panels/Sections that declare a color `variant` (and/or an `icon`) in their layout
 * props get rendered as a colored card (see `ColoredPanelWrapperComponent`). This is
 * data-driven from layout.json rather than hardcoded per field-group label, so any
 * product type can reuse the same styling for any group of fields.
 */
function getPanelWrapper(item: any): string {
  if (item.props?.variant || item.props?.icon || item.props?.background) {
    return 'coloredPanel';
  }
  return 'panel';
}

export function applySchemaToFields(fields: FormlyFieldConfig[], schema: any, opId?: string): void {
  fields.forEach((field) => {
    const fieldSchema = getSchemaForField(field.key as string, schema, opId);
    if (isValueEntity(fieldSchema)) {
      applyValueEntityToField(field, fieldSchema);
    }

    applyVisibilityState(field);

    if (field.fieldGroup && field.fieldGroup.length > 0) {
      applySchemaToFields(field.fieldGroup, schema, field.id);
    }

    if (field.fieldArray && (field.fieldArray as any).fieldGroup && (field.fieldArray as any).fieldGroup.length > 0) {
      applySchemaToFields((field.fieldArray as any).fieldGroup, schema);
    }
  });
}

/** True when any field in the input, including nested sections and collections, has rule-engine logs. */
export function checkForRuleEngineErrors(schema: any): boolean {
  if (!schema || typeof schema !== 'object') return false;
  if (isValueEntity(schema)) return Array.isArray(schema.Logs) && schema.Logs.length > 0;
  return Object.values(schema).some((child) => checkForRuleEngineErrors(child));
}

function applyVisibilityState(field: FormlyFieldConfig): void {
  const props = field.props ?? {};
  const visibilityDefined = Object.prototype.hasOwnProperty.call(props, 'visible');
  const isVisible = props['visible'] !== false;
  const isCollapsed = props['collapsed'] === true;

  const classNames = new Set((field.className || '').split(/\s+/).filter(Boolean));
  classNames.delete('formly-hidden-keep-space');
  classNames.delete('formly-hidden-collapsed');

  if (visibilityDefined && !isVisible) {
    classNames.add(isCollapsed ? 'formly-hidden-collapsed' : 'formly-hidden-keep-space');
  }

  field.className = classNames.size > 0 ? Array.from(classNames).join(' ') : undefined;
}

const MAX_SECTION_SEARCH_DEPTH = 32;

/**
 * Resolves the schema for a field key. Nested sections are searched with a depth limit and visited-object
 * tracking, so cyclic or pathologically deep input cannot exhaust the call stack.
 */
function getSchemaForField(key: string, schema: any, tabId?: string): any {
  return getSchemaForFieldRecursive(key, schema, tabId, 0, new WeakSet<object>());
}

function getSchemaForFieldRecursive(key: string, schema: any, tabId: string | undefined, depth: number, visited: WeakSet<object>): any {
  if (!key || !schema) return null;

  const fieldSchema = resolveUnderscorePath(key, schema);
  if (fieldSchema !== undefined) {
    return fieldSchema;
  }

  if (!tabId) return null;

  const operationPointSchema = findInOperationPointCollections(key, schema, tabId);
  if (operationPointSchema !== undefined) return operationPointSchema;

  return findInSections(key, schema, tabId, depth, visited);
}

/** Searches operation-point collections (dictionaries whose entries carry an Id). */
function findInOperationPointCollections(key: string, schema: any, tabId: string): any {
  const [schemaKey] = key.split('+');
  for (const prop in schema) {
    const opEntry = findOperationPointEntry(schema[prop], tabId);
    if (opEntry?.[schemaKey] !== undefined) return opEntry[schemaKey];
  }
  return undefined;
}

function findOperationPointEntry(collection: any, tabId: string): any {
  if (Array.isArray(collection) || collection === null || typeof collection !== 'object') return undefined;

  const entries = Object.values(collection) as any[];
  if (entries.length === 0 || entries[0]?.Id === undefined) return undefined;

  return entries.find((op: any) => op?.Id == tabId);
}

/** The collection may sit inside a section (e.g. Conditions.OperationPoints). */
function findInSections(key: string, schema: any, tabId: string, depth: number, visited: WeakSet<object>): any {
  if (typeof schema !== 'object' || depth >= MAX_SECTION_SEARCH_DEPTH || visited.has(schema)) return null;
  visited.add(schema);

  for (const prop in schema) {
    if (isSection(schema[prop])) {
      const found = getSchemaForFieldRecursive(key, schema[prop], tabId, depth + 1, visited);
      if (found) return found;
    }
  }
  return null;
}

function isSection(obj: any): boolean {
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj) && !isValueEntity(obj) && !('Id' in obj);
}

/** Resolves a '_'-delimited path (e.g. Conditions_Model, or just Model) against the input. */
export function resolveUnderscorePath(key: string, schema: any): any {
  let current = schema;
  for (const segment of key.split('_')) {
    if (current === null || typeof current !== 'object' || !Object.hasOwn(current, segment)) {
      return undefined;
    }
    current = current[segment];
  }
  return current;
}

const MAX_COLLECTION_ENTRY_SEARCH_DEPTH = 32;

/**
 * Returns the '_'-delimited path (e.g. Conditions_OperationPoints_0) of the collection entry with the given Id.
 * The search skips already visited objects and stops descending past a fixed depth, so cyclic or
 * pathologically deep input cannot exhaust the call stack.
 */
export function findCollectionEntryPath(schema: any, id: string): string | undefined {
  return findCollectionEntryPathRecursive(schema, id, 0, new WeakSet<object>());
}

function findCollectionEntryPathRecursive(schema: any, id: string, depth: number, visited: WeakSet<object>): string | undefined {
  if (schema === null || typeof schema !== 'object' || isValueEntity(schema)) return undefined;
  if (depth >= MAX_COLLECTION_ENTRY_SEARCH_DEPTH || visited.has(schema)) return undefined;
  visited.add(schema);

  for (const [prop, child] of Object.entries(schema)) {
    if (child !== null && typeof child === 'object' && (child as any).Id == id) return prop;
    const childPath = findCollectionEntryPathRecursive(child, id, depth + 1, visited);
    if (childPath !== undefined) return `${prop}_${childPath}`;
  }
  return undefined;
}
